import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Publisher,
  PublisherEarnings,
  PublisherPayout,
  adminListPublishers,
  adminReviewPublisher,
  adminGetPublisherEarnings,
  adminCreatePublisherPayout,
  adminListPublisherPayouts,
  adminExportPublisherPayouts,
} from '../../../../api/publisher/publisher.api';
import { Check, Download, Globe, LoaderCircle, TriangleAlert } from 'lucide-react';
import { AdminButton, AdminCard, AdminPageHeader, AdminPillTabs, AdminStatusPill } from '../../ui';
import AdminEmpty from '../../components/AdminEmpty';
import { hasStrictPermission, PERMISSION } from '../../../../utils/admin-permissions';

const NoAccess: React.FC = () => (
  <div className="p-6 text-gray-500 dark:text-gray-400">Bạn không có quyền truy cập khu vực này.</div>
);

const money = (v?: number) => `${Number(v || 0).toLocaleString('vi-VN')}đ`;
const STATUSES = ['Pending', 'Approved', 'Rejected'];
const STATUS_LABEL: Record<string, string> = { Pending: 'Chờ duyệt', Approved: 'Đang hoạt động', Rejected: 'Đã từ chối / khoá' };

const ManagementPublisherInner: React.FC = () => {
  const [status, setStatus] = useState('Pending');
  const [list, setList] = useState<Publisher[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [earn, setEarn] = useState<Record<string, PublisherEarnings>>({});
  const [payouts, setPayouts] = useState<Record<string, PublisherPayout[]>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [expFrom, setExpFrom] = useState('');
  const [expTo, setExpTo] = useState('');

  const exportCsv = async () => {
    try {
      const blob = await adminExportPublisherPayouts(expFrom || undefined, expTo || undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bang-ke-chi-tra-publisher-${expFrom || 'all'}-${expTo || 'all'}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Xuất bảng kê thất bại.');
    }
  };

  const load = () => {
    setLoading(true);
    adminListPublishers(status).then(setList).catch(() => undefined).finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  const toggle = async (id: string) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!earn[id]) {
      try {
        const [e, ps] = await Promise.all([adminGetPublisherEarnings(id), adminListPublisherPayouts(id)]);
        setEarn((p) => ({ ...p, [id]: e }));
        setPayouts((p) => ({ ...p, [id]: ps }));
      } catch { /* ignore */ }
    }
  };

  const review = async (p: Publisher, approve: boolean) => {
    let reason: string | undefined;
    if (!approve) {
      const r = window.prompt('Lý do khoá publisher này:', 'Web vi phạm chính sách');
      if (r === null) return;
      reason = r;
    }
    try {
      setBusy(p.id);
      await adminReviewPublisher(p.id, approve, reason);
      toast.success(approve ? 'Đã bỏ khoá.' : 'Đã khoá publisher.');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Thao tác thất bại.');
    } finally { setBusy(null); }
  };

  const payout = async (p: Publisher) => {
    const e = earn[p.id];
    if (!window.confirm(`Chi trả ${money(e?.availableCa)} cho "${p.name}"?${e && !e.isBusiness ? ' (Cá nhân — sẽ khấu trừ thuế TNCN)' : ''}`)) return;
    try {
      setBusy(p.id);
      const res = await adminCreatePublisherPayout(p.id);
      toast.success(`Đã chi ${money(res.amountCa)} (thuế ${money(res.taxWithheldCa)}, thực nhận ${money(res.netCa)}).`);
      const [ne, nps] = await Promise.all([adminGetPublisherEarnings(p.id), adminListPublisherPayouts(p.id)]);
      setEarn((prev) => ({ ...prev, [p.id]: ne }));
      setPayouts((prev) => ({ ...prev, [p.id]: nps }));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Chi trả thất bại.');
    } finally { setBusy(null); }
  };

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Cab Ads Network — Publisher"
        description="Quản lý web đối tác nhúng quảng cáo: đối soát doanh thu, chi trả (khấu trừ thuế cá nhân), khoá web vi phạm."
        actions={
          <AdminButton elevated icon={<Download size={18} />} onClick={exportCsv}>
            Xuất bảng kê (CSV)
          </AdminButton>
        }
      />

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <AdminPillTabs
          ariaLabel="Lọc trạng thái publisher"
          items={STATUSES.map((s) => ({ key: s, label: STATUS_LABEL[s] || s }))}
          value={status}
          onChange={setStatus}
        />
        <div className="flex items-center gap-2">
          <input type="date" aria-label="Từ ngày" value={expFrom} onChange={(e) => setExpFrom(e.target.value)} className="adm-input" style={{ width: 150 }} />
          <span style={{ color: 'var(--adm-subtle)' }}>–</span>
          <input type="date" aria-label="Đến ngày" value={expTo} onChange={(e) => setExpTo(e.target.value)} className="adm-input" style={{ width: 150 }} />
        </div>
      </div>

      {loading ? (
        <AdminEmpty icon={<LoaderCircle size={22} className="adm-btn__spin" />}>Đang tải…</AdminEmpty>
      ) : list.length === 0 ? (
        <AdminEmpty>Không có publisher nào.</AdminEmpty>
      ) : (
        <div className="adm-stack">
          {list.map((p) => {
            const e = earn[p.id];
            return (
              <AdminCard key={p.id} flush>
                <div className="flex items-center gap-3" style={{ padding: '16px 20px' }}>
                  <span
                    aria-hidden="true"
                    style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--adm-soft)', color: 'var(--adm-on-soft)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                  >
                    <Globe size={20} />
                  </span>
                  <div className="flex-1 min-w-0" style={{ display: 'grid', gap: 4 }}>
                    <div className="truncate" style={{ fontSize: 15, fontWeight: 600, color: 'var(--adm-heading)' }}>
                      {p.name} <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--adm-muted)' }}>· {p.domain}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2" style={{ fontSize: 13, color: 'var(--adm-muted)' }}>
                      <span>Chia {p.revSharePercent}%</span>
                      {p.isDomainVerified ? (
                        <AdminStatusPill tone="success" icon={<Check size={12} />}>đã xác minh domain</AdminStatusPill>
                      ) : (
                        <AdminStatusPill tone="warning" icon={<TriangleAlert size={12} />}>chưa xác minh domain</AdminStatusPill>
                      )}
                      {p.category ? <span>· {p.category}</span> : null}
                    </div>
                  </div>
                  <AdminButton size={32} variant="outline" onClick={() => toggle(p.id)}>
                    {expanded === p.id ? 'Ẩn' : 'Chi tiết'}
                  </AdminButton>
                  {p.status === 'Pending' && (
                    <>
                      <AdminButton size={32} variant="success" icon={<Check size={14} />} disabled={busy === p.id} onClick={() => review(p, true)}>Duyệt</AdminButton>
                      <AdminButton size={32} variant="danger-soft" disabled={busy === p.id} onClick={() => review(p, false)}>Từ chối</AdminButton>
                    </>
                  )}
                  {p.status === 'Approved' && (
                    <AdminButton size={32} variant="danger-soft" disabled={busy === p.id} onClick={() => review(p, false)}>Khoá</AdminButton>
                  )}
                  {p.status === 'Rejected' && (
                    <AdminButton size={32} variant="success" icon={<Check size={14} />} disabled={busy === p.id} onClick={() => review(p, true)}>Duyệt</AdminButton>
                  )}
                </div>

                {expanded === p.id && (
                  <div className="adm-stack" style={{ borderTop: '1px solid var(--adm-line)', padding: 20, gap: 16 }}>
                    <div className="flex flex-wrap gap-x-6 gap-y-2" style={{ fontSize: 14, color: 'var(--adm-text)' }}>
                      <div><span className="adm-muted">Có thể chi:</span> <b>{money(e?.availableCa)}</b></div>
                      <div><span className="adm-muted">Đang giữ:</span> <span>{money(e?.heldCa)}</span></div>
                      <div><span className="adm-muted">Đã chi:</span> <span>{money(e?.paidCa)}</span></div>
                      <div><span className="adm-muted">Tổng kiếm:</span> <span>{money(e?.totalEarnedCa)}</span></div>
                      <div><span className="adm-muted">HT/Click:</span> <span>{(e?.totalImpressions ?? 0).toLocaleString('vi-VN')}/{(e?.totalClicks ?? 0).toLocaleString('vi-VN')}</span></div>
                      <div><span className="adm-muted">Loại:</span> <span>{e?.isBusiness ? 'Doanh nghiệp' : 'Cá nhân'}</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <AdminButton
                        size={36}
                        disabled={busy === p.id || !e || e.availableCa < (e?.minPayoutCa ?? 0)}
                        onClick={() => payout(p)}
                      >
                        Chi trả {money(e?.availableCa)}
                      </AdminButton>
                      {e && e.availableCa < e.minPayoutCa && (
                        <span className="adm-muted" style={{ fontSize: 13 }}>Chưa đạt ngưỡng tối thiểu {money(e.minPayoutCa)}</span>
                      )}
                    </div>

                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--adm-label)', marginBottom: 8 }}>Lịch sử chi trả</div>
                      {(payouts[p.id] || []).length === 0 ? (
                        <p className="adm-muted" style={{ fontSize: 13, margin: 0 }}>Chưa có lần chi nào.</p>
                      ) : (
                        <div className="adm-panel" style={{ padding: 0, overflow: 'hidden' }}>
                          <div className="adm-table-wrap">
                            <table className="adm-table">
                              <thead><tr><th>Ngày</th><th className="adm-num">Số tiền</th><th className="adm-num">Thuế</th><th className="adm-num">Thực nhận</th></tr></thead>
                              <tbody>
                                {(payouts[p.id] || []).map((po) => (
                                  <tr key={po.id}>
                                    <td>{new Date(po.createdAt).toLocaleDateString('vi-VN')}</td>
                                    <td className="adm-num">{money(po.amountCa)}</td>
                                    <td className="adm-num">{money(po.taxWithheldCa)}</td>
                                    <td className="adm-num" style={{ fontWeight: 600 }}>{money(po.netCa)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </AdminCard>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ManagementPublisher: React.FC = () =>
  hasStrictPermission(PERMISSION.AdsManage) ? <ManagementPublisherInner /> : <NoAccess />;

export default ManagementPublisher;
